import axios from "axios";

const getBaseURL = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return process.env.REACT_APP_BACKEND_URL;
};

export const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true, // ✅ ensures cookies are sent
});

// Request deduplication - prevent duplicate simultaneous requests
const pendingRequests = new Map();

const getRequestKey = (config) => {
  return `${config.method.toUpperCase()}_${config.url}_${JSON.stringify(config.data || {})}`;
};

api.interceptors.request.use((config) => {
  const key = getRequestKey(config);

  // Check if identical request is already pending
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  // Store this request promise
  const promise = Promise.resolve(config);
  pendingRequests.set(key, promise);

  return config;
});

// Retry logic with exponential backoff for 429 errors
const MAX_RETRIES = 3;
const INITIAL_DELAY = 1000; // 1 second

api.interceptors.response.use(
  (response) => {
    // Clear pending request on success
    const key = getRequestKey(response.config);
    pendingRequests.delete(key);
    return response;
  },
  async (error) => {
    const config = error.config;

    // Clear pending request on any response
    const key = getRequestKey(config);
    pendingRequests.delete(key);

    // Only retry on 429 (too many requests)
    if (error.response?.status === 429 && config.__retryCount === undefined) {
      config.__retryCount = 0;
    }

    if (error.response?.status === 429 && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;

      // Exponential backoff: 1s, 2s, 4s
      const delay = INITIAL_DELAY * Math.pow(2, config.__retryCount - 1);

      console.warn(
        `Rate limited (429). Retrying request ${config.__retryCount}/${MAX_RETRIES} after ${delay}ms`,
        { method: config.method, url: config.url },
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
      return api.request(config);
    }

    // Log errors (except rate limits which are already logged)
    if (error.response?.status !== 429) {
      console.error("API Error:", error.message);
    }

    return Promise.reject(error);
  },
);
